import { TEnumName, useEnum } from "@/api/services/global/enum";
import { IconClose, IconExpandMore } from "@/assets/icons";
import { useFormField } from "@/components/elements/FormField/FormField";
import { IconBox } from "@/components/elements/IconBox";
import { background, black, blue, divider, lightBlue, state, text } from "@/theme/colors";
import { typography } from "@/theme/typography";
import { rounded } from "@/theme/variables";
import { TRequiredOne } from "@/types/common";
import { TLanguageCode } from "@/types/enum";
import { findLabelByValue } from "@/utils/object";
import {
  Box,
  CircularProgress,
  FormHelperText,
  MenuItem,
  Select as MuiSelect,
  SelectChangeEvent,
  SelectProps,
  Typography,
  css,
  styled,
} from "@mui/material";
import { FC, useMemo } from "react";

export type TSelectColorSize = "md" | "lg";

type TCustomOption = {
  label: string;
  value: string | number;
  color?: string;
  hidden?: boolean;
};

export type TSelectColorProps = {
  variant?: "standard" | "pill";
  size?: TSelectColorSize;
  readonly?: boolean;
  clearable?: boolean;
  hideIcon?: boolean;
  previousValue?: string | number;
  helperText?: string;
  nonControl?: boolean;
  loading?: boolean;
  disabledValues?: string[];
  lang?: TLanguageCode;
  colorPrevious?: string;
  optionColors?: {
    [key: string]: {
      bgColor: string;
      color: string;
    };
  };
  onChange?: (value: string) => void;
} & Omit<SelectProps, "size" | "children" | "readOnly" | "multiple" | "onChange" | "variant"> &
  TRequiredOne<{ options: TCustomOption[]; enumName: TEnumName }>;

export const SelectColor: FC<TSelectColorProps> = ({
  variant = "standard",
  size = "md",
  readonly = false,
  previousValue = false,
  nonControl = false,
  placeholder = "選択してください",
  clearable = true,
  helperText,
  options,
  enumName,
  lang,
  loading = false,
  disabled,
  disabledValues = [],
  colorPrevious = lightBlue[60],
  optionColors,
  hideIcon = false,
  ...rest
}) => {
  const { field, fieldState } = useFormField(nonControl);
  const { data, isLoading } = useEnum(enumName, lang ? { lang } : undefined);
  const value = field ? field.value : rest.value;

  const isPrevious = useMemo(() => {
    if (fieldState?.isDirty === false) return true;
    return Boolean(previousValue) && value === previousValue;
  }, [fieldState?.isDirty, previousValue, value]);

  const selectOptions = useMemo(() => options ?? data ?? [], [options, data]);

  const selected = useMemo(() => {
    return [value].flat().flatMap((v) => {
      if (v === undefined || v === null) return [];
      const label = findLabelByValue(selectOptions, v);
      if (!label) return [];
      return label;
    });
  }, [value, selectOptions]);

  const selectedOption = useMemo(() => {
    if (value === undefined || value === null) return null;
    return selectOptions.find((option: TCustomOption) => option.value === value);
  }, [value, selectOptions]);

  const selectedColors = useMemo(() => {
    if (!optionColors) {
      return { bgColor: background.white, color: text.primary };
    }

    if (!selectedOption) {
      return { bgColor: background.white, color: text.primary };
    }

    const hasColor = "color" in selectedOption && selectedOption.color;
    if (!hasColor) {
      return { bgColor: background.white, color: text.primary };
    }

    return optionColors[selectedOption.color as string] || { bgColor: background.white, color: text.primary };
  }, [selectedOption, optionColors]);

  const IconComponent = useMemo(() => {
    if (hideIcon) return () => null;

    if (variant === "pill") {
      return () => (
        // pointer-events: none on the wrapper so clicks on the icon area fall through to the
        // underlying MuiSelect-select trigger (which opens the menu). The close IconBox below
        // re-enables pointer-events so it stays clickable.
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            position: "absolute",
            right: 0,
            top: "50%",
            transform: "translateY(-50%)",
            pr: 1,
            pointerEvents: "none",
          }}
        >
          {!(isLoading || loading) && (
            <Box sx={{ display: "flex", color: selectedColors.color || text.primary }}>
              <IconExpandMore />
            </Box>
          )}
          {clearable && selected.length > 0 && !readonly && !disabled && (
            <IconBox
              sx={{ cursor: "pointer", pointerEvents: "auto", zIndex: 1 }}
              onMouseDown={(e) => {
                e.stopPropagation();
              }}
              onClick={(e) => {
                e.stopPropagation();
                rest.onChange?.("");
                field?.onChange(null);
                field?.onBlur();
              }}
            >
              <IconClose fontSize={16} color={selectedColors.color || text.primary} />
            </IconBox>
          )}
        </Box>
      );
    }

    if (!clearable) return;
    if (selected.length) {
      return () => (
        <IconBox
          sx={{ mr: 1 }}
          onClick={() => {
            rest.onChange?.("");
            field?.onChange(null);
            field?.onBlur();
          }}
        >
          {!readonly && !disabled && <IconClose fontSize={20} color={text.primary} />}
        </IconBox>
      );
    }
    return isLoading || loading ? () => null : IconExpandMore;
  }, [isLoading, loading, selected, readonly, disabled, clearable, hideIcon, variant, selectedColors.color, rest.onChange, field]);

  const onChange = (e: SelectChangeEvent<unknown>) => {
    rest.onChange?.(e.target.value as string);
    field?.onChange(e);
    field?.onBlur();
  };

  return (
    <Box position="relative">
      <StyledSelect
        selectVariant={variant}
        error={Boolean(fieldState?.error)}
        selectSize={size}
        readonly={readonly}
        previous={isPrevious}
        hasValue={selected.length > 0}
        displayEmpty
        IconComponent={IconComponent}
        {...rest}
        {...field}
        value={value ?? null}
        disabled={disabled}
        colorPrevious={colorPrevious}
        selectedBgColor={selectedColors.bgColor}
        selectedColor={selectedColors.color}
        onChange={onChange}
        renderValue={() => {
          if (selected.length === 0) {
            return (
              <Typography variant="cap12" color={`${text.tertiary}!important`}>
                {placeholder}
              </Typography>
            );
          }

          return selected.join(", ");
        }}
      >
        {selectOptions.map((option: TCustomOption) => {
          const isDisable = "hidden" in option && option.hidden === true;
          const isHidden = isDisable && value !== option.value;
          const color = option.color ? optionColors?.[option.color]?.color : "#000";
          const bgColor = option.color ? optionColors?.[option.color]?.bgColor : "#fff";

          return (
            !isHidden && (
              <MenuItem
                key={option.value}
                value={option.value}
                disabled={isDisable || disabledValues.includes(option?.value?.toString())}
                sx={{ bgcolor: bgColor, color: color + "!important", mt: "5px", border: bgColor === "#FFFFFF" ? `1px solid ${black[95]}` : "none" }}
              >
                {option.label}
              </MenuItem>
            )
          );
        })}
      </StyledSelect>
      {(isLoading || loading) && (
        <LoadingWrap>
          <CircularProgress color="inherit" size={14} />
        </LoadingWrap>
      )}
      {Boolean(helperText) && <FormHelperText>{helperText}</FormHelperText>}
    </Box>
  );
};

const options = {
  shouldForwardProp: (propName: string) =>
    !["selectVariant", "selectSize", "previous", "readonly", "hasValue", "selectedBgColor", "selectedColor", "colorPrevious"].includes(propName),
};

const StyledSelect = styled(MuiSelect, options)<{
  selectVariant: "standard" | "pill";
  selectSize: TSelectColorSize;
  readonly: boolean;
  previous: boolean;
  hasValue: boolean;
  colorPrevious?: string;
  selectedBgColor?: string;
  selectedColor?: string;
}>`
  &.MuiInputBase-root {
    position: relative;
    background: ${background.white};
    border-radius: ${rounded.md} !important;
    &::placeholder {
      color: ${text.tertiary};
    }
    fieldset {
      border-color: ${divider.middle};
      border-radius: ${rounded.md};
      border-width: 1px !important;
    }
    &:hover,
    &.Mui-focused {
      fieldset {
        border-color: ${blue[70]};
      }
    }
    &.Mui-focused {
      &::after {
        content: "";
        position: absolute;
        top: -4px;
        left: -4px;
        width: 100%;
        height: 100%;
        padding: 2px;
        box-sizing: content-box;
        border-radius: ${rounded.md};
        z-index: -1;
      }
    }

    &.Mui-error {
      background: ${background.error};
      fieldset {
        border-color: ${state.error_1}!important;
      }
    }
    &.Mui-disabled {
      background: ${background.disable};
      fieldset {
        border-color: ${divider.middle}!important;
      }
    }
    .MuiSelect-icon {
      transform: none !important;
    }

    ${({ selectVariant }) =>
      selectVariant === "pill" &&
      css`
        border-radius: 100px !important;
        fieldset {
          border-radius: 100px !important;
        }
      `}

    ${({ selectSize, hasValue, selectVariant }) => {
      if (selectSize === "md") return MediumInput(hasValue, selectVariant);
      if (selectSize === "lg") return LargeInput;
      return MediumInput(hasValue, selectVariant);
    }};
    ${({ readonly }) => readonly && readonlyInput};
    ${({ previous, colorPrevious }) =>
      previous &&
      css`
        .MuiSelect-select {
          color: ${colorPrevious};
        }
      `};

    ${({ selectedBgColor, selectedColor, selectVariant }) =>
      selectedBgColor &&
      selectedColor &&
      css`
        ${selectVariant === "pill"
          ? css`
              background-color: ${selectedBgColor} !important;
              .MuiInputBase-input {
                background-color: transparent !important;
                color: ${selectedColor} !important;
              }
              ${selectedBgColor.toUpperCase() === "#FFFFFF" &&
              css`
                /* White pills blend with the page; add a subtle outline so they remain visible.
                   !important wins over external sx overrides (e.g. DocumentTypeTagsRow's border:none). */
                .MuiOutlinedInput-notchedOutline {
                  border: 1px solid ${black[95]} !important;
                }
              `}
            `
          : css`
              .MuiInputBase-input {
                background-color: ${selectedBgColor} !important;
                color: ${selectedColor} !important;
              }
            `}
      `};
  }
`;

const readonlyInput = css`
  background: ${background.disable};
  pointer-events: none;
  fieldset {
    border-color: ${divider.middle}!important;
    border-radius: ${rounded.md} !important;
  }
  &::placeholder {
    opacity: 0;
  }
  .MuiInputBase-input {
    border-radius: ${rounded.md} !important;
  }
`;

const MediumInput = (hasValue: boolean, selectVariant: "standard" | "pill") => css`
  .MuiInputBase-input {
    padding: 4px ${hasValue && selectVariant === "pill" ? "44px" : "32px"} 4px ${selectVariant === "pill" ? "6px" : "10px"};
    border-radius: ${selectVariant === "pill" ? "100px !important" : hasValue ? `${rounded.md} !important` : "none !important"};
    ${css(typography.cap12)};
  }
`;

const LargeInput = css`
  .MuiInputBase-input {
    padding: 12px 8px;
    ${css(typography.body14)};
  }
`;

const LoadingWrap = styled(Box)`
  position: absolute;
  display: block;
  top: 0;
  right: 0;
  background-color: inherit;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 30px;
`;
